import { useEffect, useState } from "react";
import { X, ZoomIn, Download } from "lucide-react";
import api, { API } from "@/lib/api";

/**
 * Lightbox / preview overlay for files (PDFs and images).
 * Loads file as blob (so auth header works) and shows it inline.
 */
export default function FilePreview({ file, onClose }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) return;
    let active = true;
    let blobUrl = null;
    setUrl(null);
    setError(null);

    api.get(`/files/${file.id}/download`, { responseType: "blob" })
      .then((r) => {
        if (!active) return;
        blobUrl = URL.createObjectURL(r.data);
        setUrl(blobUrl);
      })
      .catch(() => {
        if (active) setError("Cannot load file");
      });

    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [file]);

  useEffect(() => {
    if (!file) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [file, onClose]);

  if (!file) return null;

  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = (file.content_type || "").includes("pdf");

  const token = localStorage.getItem("sodimate_token");
  const downloadHref = `${API}/files/${file.id}/download?auth=${encodeURIComponent(token)}`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 grid place-items-center p-2 sm:p-4" onClick={onClose} data-testid="file-preview">
      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
        <a
          href={downloadHref}
          download={file.filename}
          onClick={(e) => e.stopPropagation()}
          className="bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 flex items-center gap-1 backdrop-blur"
          data-testid="preview-download"
        >
          <Download size={12} /> Download
        </a>
        <button
          onClick={onClose}
          className="bg-white/10 hover:bg-white/20 text-white p-1.5 backdrop-blur"
          data-testid="preview-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="absolute top-3 left-3 text-white/80 text-xs flex items-center gap-2 backdrop-blur bg-black/30 px-3 py-1.5">
        <span className="font-mono uppercase tracking-wider">{file.filename}</span>
      </div>

      <div onClick={(e) => e.stopPropagation()} className="max-w-[95vw] max-h-[90vh]">
        {error && <div className="text-white">{error}</div>}
        {!url && !error && <div className="text-white/60 text-sm">Loading…</div>}

        {url && isImage && (
          <img src={url} alt={file.filename} className="max-w-[95vw] max-h-[90vh] object-contain" />
        )}
        {url && isPdf && (
          <iframe
            src={url}
            title={file.filename}
            className="bg-white"
            style={{ width: "min(95vw, 1100px)", height: "min(90vh, 1500px)" }}
          />
        )}
        {url && !isImage && !isPdf && (
          <div className="bg-white p-8 text-center">
            <ZoomIn size={32} className="mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-gray-600">No inline preview for this file type.</p>
            <a href={downloadHref} download={file.filename} className="btn-primary mt-4 inline-flex">
              <Download size={14} /> Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
